import { useApiQuery } from '../useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { jobEndpoints } from '@/lib/api/endpoints';
import { normalizeJob } from '@/lib/normalizeJob';
import { jobKeys } from '@/lib/queryKeys';
import type { Job } from '@/types/job';

/**
 * Fetches a single job by ID from the operations microservice.
 *
 * `showErrorToast: false` suppresses the global error toast — use it when a
 * caller resolves a job id that may be stale/inaccessible and degrades silently
 * (e.g. an invoice's loose `jobId`), so a 404 doesn't pop a toast the UI is
 * already hiding.
 */
export function useJob(id: string, options?: { showErrorToast?: boolean }) {
  return useApiQuery<Job>({
    queryKey: jobKeys.detail(id),
    queryFn: async () => normalizeJob(await apiGet(operationsClient, jobEndpoints.detail(id))),
    enabled: Boolean(id),
    // Issuance and payment advance billing stages asynchronously through the outbox.
    // Keep visible completed/invoiced jobs current until settlement closes them.
    refetchInterval: (query) =>
      ['COMPLETED', 'INVOICED'].includes(
        String(query.state.data?.statusPhase ?? query.state.data?.status).toUpperCase(),
      )
        ? 5000
        : false,
    showErrorToast: options?.showErrorToast,
  });
}
